import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Users,
  Briefcase,
  Clock,
  Calendar,
  ArrowRight,
  Eye,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { ShortlistedFreelancersTable } from "@/components/dashboard/ShortlistedFreelancersTable";
import { PlataformaCargaSection } from "@/components/dashboard/PlataformaCargaSection";
import { AgenciaFunilTable } from "@/components/dashboard/AgenciaFunilTable";

const fetchFreelancersStats = async () => {
  const { count: totalFreelancers, error: countError } = await supabase
    .from("freelancers")
    .select("*", { count: "exact", head: true });

  if (countError) {
    console.error("Erro ao buscar total de freelancers:", countError);
    return { total: 0, novos: 0 };
  }

  const dataLimite = new Date();
  dataLimite.setDate(dataLimite.getDate() - 90);
  const { count: novosFreelancers, error: novosError } = await supabase
    .from("freelancers")
    .select("*", { count: "exact", head: true })
    .gte("created_at", dataLimite.toISOString());

  if (novosError) {
    console.error("Erro ao buscar novos freelancers:", novosError);
    return { total: totalFreelancers || 0, novos: 0 };
  }

  return {
    total: totalFreelancers || 0,
    novos: novosFreelancers || 0,
  };
};

const fetchVagasStats = async () => {
  const { data: vagas } = await supabase.from("vagas").select("*");

  const total = vagas?.length || 0;
  const abertas = vagas?.filter((v) => v.status === "entrada").length || 0;
  const emAndamento = vagas?.filter(
    (v) =>
      v.status === "triagem" ||
      v.status === "shortlist" ||
      v.status === "checagem_disponibilidade"
  ).length || 0;
  const preenchidas = vagas?.filter((v) => v.status === "aprovado").length || 0;

  const vagasPorMes = vagas?.reduce((contagemPorMes: Record<string, number>, vaga) => {
    const mes = new Date(vaga.created_at || "").toLocaleString("pt-BR", {
      month: "short",
    });
    contagemPorMes[mes] = (contagemPorMes[mes] || 0) + 1;
    return contagemPorMes;
  }, {});

  const evolucaoMensal = Object.entries(vagasPorMes || {}).map(
    ([mes, quantidade]) => ({
      mes,
      quantidade,
    })
  );

  return {
    total,
    abertas,
    emAndamento,
    preenchidas,
    evolucaoMensal,
  };
};

const AreasBadges = ({ areas }: { areas: string[] | null }) => {
  if (!areas || areas.length === 0) return <span className="text-muted-foreground">-</span>;
  
  const maxVisible = 3;
  const visibleAreas = areas.slice(0, maxVisible);
  const hiddenAreas = areas.slice(maxVisible);
  
  return (
    <div className="flex flex-wrap gap-1">
      {visibleAreas.map((area, index) => (
        <Badge key={index} variant="secondary" className="text-xs">
          {area}
        </Badge>
      ))}
      {hiddenAreas.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="text-xs cursor-help">
                +{hiddenAreas.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-wrap gap-1 max-w-[200px]">
                {hiddenAreas.map((area, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {area}
                  </Badge>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

function DashboardCompleto() {
  const navigate = useNavigate();
  
  const { data: freelancersStats } = useQuery({
    queryKey: ["freelancersStats"],
    queryFn: fetchFreelancersStats,
  });

  const { data: vagasStats } = useQuery({
    queryKey: ["vagasStats"],
    queryFn: fetchVagasStats,
  });

  const { data: recentFreelancers } = useQuery({
    queryKey: ["recentFreelancers"],
    queryFn: async () => {
      const { data } = await supabase
        .from("freelancers")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(5);
      return data;
    },
  });

  // Hex reais por stat: `stat.color` (ex. "text-blue-500") serve pro ícone via
  // classe Tailwind, mas NÃO é cor CSS válida para borderLeftColor inline.
  const STAT_BORDER: Record<string, string> = {
    "text-blue-500": "#3b82f6",
    "text-green-500": "#22c55e",
    "text-yellow-500": "#eab308",
    "text-purple-500": "#a855f7",
  };

  const stats = [
    {
      title: "Total de Freelancers",
      value: freelancersStats?.total || 0,
      description: `+${freelancersStats?.novos || 0} nos últimos 90 dias`,
      icon: Users,
      color: "text-blue-500",
      link: "/freelancers",
    },
    {
      title: "Vagas Abertas",
      value: vagasStats?.abertas || 0,
      description: "Aguardando triagem",
      icon: Briefcase,
      color: "text-green-500",
      link: "/vagas",
    },
    {
      title: "Vagas em Andamento",
      value: vagasStats?.emAndamento || 0,
      description: "Em processo de seleção",
      icon: Clock,
      color: "text-yellow-500",
      link: "/vagas",
    },
    {
      title: "Vagas Preenchidas",
      value: vagasStats?.preenchidas || 0,
      description: "Tempo médio: 8.5 dias",
      icon: Calendar,
      color: "text-purple-500",
      link: "/vagas",
    },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const chartConfig = {
    vagas: {
      label: "Vagas",
      theme: {
        light: "#8884d8",
        dark: "#8884d8",
      },
    },
  };

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        title="Dashboard"
        subtitle="Visão geral do sistema Corisco Frilas"
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card
            key={stat.title}
            className="hover:shadow-lg transition-shadow border-l-4 cursor-pointer"
            style={{ borderLeftColor: STAT_BORDER[stat.color] ?? "hsl(var(--border))" }}
            onClick={() => navigate(stat.link)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className={cn("h-4 w-4", stat.color)} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{stat.description}</p>
                <Button variant="ghost" size="sm" className="h-8">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Evolução de Vagas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full overflow-hidden">
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={vagasStats?.evolucaoMensal || []}
                    margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                  >
                    <XAxis
                      dataKey="mes"
                      angle={-45}
                      textAnchor="end"
                      height={50}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <YAxis 
                      width={40}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => Math.floor(value).toString()}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="quantidade"
                      fill="#8884d8"
                      name="Vagas"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}

                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status das Vagas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "Abertas",
                          value: vagasStats?.abertas || 0,
                        },
                        {
                          name: "Em Andamento",
                          value: vagasStats?.emAndamento || 0,
                        },
                        {
                          name: "Preenchidas",
                          value: vagasStats?.preenchidas || 0,
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-5">
        <div className="xl:col-span-2">
          <PlataformaCargaSection />
        </div>
        <div className="xl:col-span-3">
          <AgenciaFunilTable />
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Freelancers Recentes</h2>
        <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Pretensão Salarial</TableHead>
                  <TableHead>Interesse em Mudar de Emprego</TableHead>
                  <TableHead>Data de Cadastro</TableHead>
                  <TableHead className="w-[100px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentFreelancers?.map((freelancer) => (
                  <TableRow key={freelancer.id}>
                    <TableCell className="font-medium">
                      {freelancer.nome}
                    </TableCell>
                    <TableCell>
                      <AreasBadges areas={freelancer.areas} />
                    </TableCell>
                    <TableCell>{freelancer.pretensao_salarial || "-"}</TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge
                              variant={freelancer.interesse_em_mudar_emprego ? "default" : "secondary"}
                              className="cursor-help"
                            >
                              {freelancer.interesse_em_mudar_emprego ? "Sim" : "Não"}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1 text-xs">
                              <div>
                                <span className="font-semibold">Interesse vaga fixa:</span>{" "}
                                {freelancer.interesse_vaga_fixa}
                              </div>
                              <div>
                                <span className="font-semibold">Tipo de atuação:</span>{" "}
                                {freelancer.tipo_atuacao || "Não informado"}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(freelancer.created_at),
                        "dd/MM/yyyy",
                        { locale: ptBR }
                      )}
                    </TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="icon">
                        <Link
                          to={`/freelancers/${freelancer.id}`}
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </div>
      </section>

      <ShortlistedFreelancersTable />
    </div>
  );
}

function DashboardSimplificado() {
  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center p-6 fade-in">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center space-y-4">
          <CardTitle className="text-4xl font-bold">
            Bem-vindo ao Corisco Frilas
          </CardTitle>
          <p className="text-muted-foreground text-lg">
            Gerencie e avalie perfis de freelancers de forma eficiente
          </p>
        </CardHeader>
        <CardContent className="flex justify-center pt-6">
          <Button asChild size="lg" className="gap-2">
            <Link to="/avaliacoes/perfil-freelancer">
              Ir para Avaliações
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { profileName, profileSlug, isLoading } = useUserPermissions();
  const navigate = useNavigate();

  // Redirect para dashboard de agência se for perfil de agência
  if (!isLoading && profileSlug?.includes("agencia")) {
    navigate("/agencia/dashboard", { replace: true });
    return null;
  }

  // Mostrar loading enquanto carrega as permissões
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  // Verificar se é Admin ou Membro
  const isAdminOrMember = ["admin", "membro"].includes(profileName?.toLowerCase() || "");

  // Renderizar dashboard apropriado
  if (isAdminOrMember) {
    return <DashboardCompleto />;
  } else {
    return <DashboardSimplificado />;
  }
}
